import {FC, useEffect, useMemo, useState} from 'react'
import {createPortal} from 'react-dom'
import {Link} from 'react-router-dom'
import {ListToolbar, FilterSelect} from '../../components/ListToolbar'
import {
  api,
  ApiError,
  Bed,
  BedTypeCatalog,
  Listing,
  ListingInput,
  listingImageSrc,
  ListingRule,
} from '../../services/api'
import {Spinner} from '../../components/Spinner'
import {useToast} from '../../components/Toast'
import {confirmDialog} from '../../components/dialog'
import {PageHead} from '../../components/PageHead'

const ACCENT = '#039540'
const INK = '#1b1b29'
const MUTED = '#7e8299'

const slugify = (s: string) =>
  s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')

const EMPTY: ListingInput = {
  slug: '',
  name: '',
  title: '',
  cadence: 'Monthly',
  price: '$0.00',
  priceUnit: 'Monthly',
  beds: '1 Bed',
  baths: '1 Bath',
  guests: '1 Guest',
  blurb: '',
  features: [],
  description: '',
  bedrooms: 1,
  bedsCount: 1,
  size: '',
  type: 'Private bunk',
  address: '',
  priceOptions: [],
  deposit: '$0',
  detailAmenities: [],
  rules: [],
  gallery: [],
  published: true,
  sortOrder: 0,
}

const ListingsPage: FC = () => {
  const toast = useToast()
  const [listings, setListings] = useState<Listing[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [editing, setEditing] = useState<{listing: Listing | null} | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [visibility, setVisibility] = useState('')

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase()
    return listings.filter((l) => {
      const matchesSearch =
        q === '' ||
        l.name.toLowerCase().includes(q) ||
        l.title.toLowerCase().includes(q) ||
        l.slug.toLowerCase().includes(q)
      const matchesVisibility =
        visibility === '' ||
        (visibility === 'published' ? l.published === true : l.published === false)
      return matchesSearch && matchesVisibility
    })
  }, [listings, search, visibility])

  const load = async () => {
    setLoading(true)
    setError(null)
    try {
      setListings(await api.adminListings())
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Failed to load listings')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load()
  }, [])

  const togglePublish = async (l: Listing) => {
    setBusyId(l.id)
    try {
      const updated = await api.updateListing(l.id, {published: !l.published})
      setListings((list) => list.map((x) => (x.id === l.id ? updated : x)))
      toast.success(`${l.name} ${updated.published ? 'published' : 'unpublished'}.`)
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Could not update')
    } finally {
      setBusyId(null)
    }
  }

  const remove = async (l: Listing) => {
    const ok = await confirmDialog({
      title: `Delete “${l.name}”?`,
      text: 'This permanently removes the listing from the public site.',
      confirmText: 'Delete',
      danger: true,
    })
    if (!ok) return
    setBusyId(l.id)
    try {
      await api.deleteListing(l.id)
      setListings((list) => list.filter((x) => x.id !== l.id))
      toast.success(`${l.name} deleted.`)
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Could not delete')
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div>
      <PageHead
        icon='🏠'
        eyebrow='Operations'
        title='Rooms'
        sub='Manage the Rooms & Plans shown on the public site.'
        action={
          <div style={{display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap'}}>
            <Link to='/beds' style={{fontSize: 13, fontWeight: 600, color: '#fff', border: '1px solid rgba(255,255,255,0.6)', borderRadius: 8, padding: '9px 14px', textDecoration: 'none', display: 'inline-block'}}>Manage beds →</Link>
            <button onClick={() => setEditing({listing: null})} className='ccp-cta-light' style={{fontSize: 13, fontWeight: 700, color: ACCENT, background: '#fff', border: 'none', borderRadius: 8, padding: '9px 16px', cursor: 'pointer'}}>+ New room</button>
          </div>
        }
      />

      <ListToolbar search={search} onSearch={setSearch} placeholder='Search rooms…'>
        <FilterSelect
          value={visibility}
          onChange={setVisibility}
          ariaLabel='Visibility'
          options={[
            {label: 'All', value: ''},
            {label: 'Published', value: 'published'},
            {label: 'Draft', value: 'draft'},
          ]}
        />
      </ListToolbar>

      {loading ? (
        <div style={{padding: 40, textAlign: 'center', color: MUTED}}>Loading listings…</div>
      ) : error ? (
        <div style={{padding: 40, textAlign: 'center', color: '#d9214e'}}>{error}</div>
      ) : listings.length === 0 ? (
        <div style={{padding: 40, textAlign: 'center', color: MUTED}}>No listings yet. Create one to get started.</div>
      ) : visible.length === 0 ? (
        <div style={{padding: 40, textAlign: 'center', color: MUTED}}>No rooms match your search.</div>
      ) : (
        <div className='row g-4 ccp-stagger'>
          {visible.map((l) => {
            const busy = busyId === l.id
            return (
              <div className='col-12 col-md-6 col-lg-4' key={l.id}>
                <div className='ccp-lift ccp-zoom' style={{background: '#fff', borderRadius: 16, overflow: 'hidden', boxShadow: '0 6px 18px rgba(27,27,41,0.06)', height: '100%', display: 'flex', flexDirection: 'column'}}>
                  <div style={{position: 'relative', height: 160, background: '#f1f3f7'}}>
                    {l.gallery[0] && (
                      <img src={listingImageSrc(l.gallery[0])} alt={l.title} style={{width: '100%', height: '100%', objectFit: 'cover', display: 'block'}} />
                    )}
                    <span style={{position: 'absolute', top: 12, left: 12, display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: l.published ? '#1f9d5b' : MUTED, background: '#fff', borderRadius: 999, padding: '5px 11px 5px 9px', boxShadow: '0 2px 8px rgba(8,10,14,0.18)'}}>
                      <span style={{width: 7, height: 7, borderRadius: '50%', background: l.published ? '#1f9d5b' : MUTED}} />
                      {l.published ? 'Published' : 'Draft'}
                    </span>
                  </div>
                  <div style={{padding: '16px 18px', flex: 1}}>
                    <div style={{display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8}}>
                      <h3 style={{fontSize: 17, fontWeight: 700, color: INK, margin: 0}}>{l.title}</h3>
                      <span style={{color: ACCENT, fontWeight: 800, fontSize: 15, whiteSpace: 'nowrap'}}>
                        {l.bedType ? `$${Number(l.bedType.pricePerMonth).toFixed(0)}` : '—'}
                      </span>
                    </div>
                    <div style={{color: MUTED, fontSize: 13, marginTop: 4}}>
                      {l.bedType ? `${l.bedType.label} · /${l.priceUnit}` : 'No bed type'} · order {l.sortOrder}
                    </div>
                  </div>
                  <div style={{padding: '12px 16px', borderTop: '1px solid #f1f3f7', display: 'flex', gap: 8}}>
                    <button onClick={() => setEditing({listing: l})} disabled={busy} style={{...btnGhost, flex: 1}}>Edit</button>
                    <button onClick={() => togglePublish(l)} disabled={busy} style={{...btnGhost, flex: 1}}>{busy ? <Spinner size={13} /> : l.published ? 'Unpublish' : 'Publish'}</button>
                    <button onClick={() => remove(l)} disabled={busy} style={btnDanger}>Delete</button>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {editing && (
        <ListingEditor
          listing={editing.listing}
          onClose={() => setEditing(null)}
          onSaved={(saved, isNew) => {
            setListings((list) => (isNew ? [...list, saved] : list.map((x) => (x.id === saved.id ? saved : x))))
            setEditing(null)
            toast.success(`${saved.name} ${isNew ? 'created' : 'saved'}.`)
          }}
        />
      )}
    </div>
  )
}

// ─── Editor modal ───────────────────────────────────────────────────────
const ListingEditor: FC<{
  listing: Listing | null
  onClose: () => void
  onSaved: (saved: Listing, isNew: boolean) => void
}> = ({listing, onClose, onSaved}) => {
  const toast = useToast()
  const isNew = !listing
  const [form, setForm] = useState<ListingInput>(
    listing ? {...listing, bedTypeId: listing.bedType?.id} : {...EMPTY},
  )
  const [bedTypes, setBedTypes] = useState<BedTypeCatalog[]>([])
  const [allBeds, setAllBeds] = useState<Bed[]>([])
  const [bedIds, setBedIds] = useState<string[]>([])
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)

  useEffect(() => {
    api.bedTypes().then(setBedTypes).catch(() => undefined)
    api.listBeds().then((bs) => {
      setAllBeds(bs)
      if (listing) setBedIds(bs.filter((b) => b.room?.id === listing.id).map((b) => b.id))
    }).catch(() => undefined)
  }, [listing])

  // Bed count + the "Beds" label are derived from the selected beds — never typed.
  useEffect(() => {
    const n = bedIds.length
    setForm((f) => ({...f, bedsCount: n, beds: `${n} Bed${n === 1 ? '' : 's'}`}))
  }, [bedIds])

  // Price unit follows the billing period (Monthly → "month", Per day → "day").
  useEffect(() => {
    setForm((f) => ({...f, priceUnit: f.cadence === 'Per day' ? 'day' : 'month'}))
  }, [form.cadence])

  // Beds you can add: unassigned (or already in this room) AND matching the
  // room's selected bed type — never one of another type or another room.
  const selectedTypeKey = bedTypes.find((t) => t.id === form.bedTypeId)?.key
  const eligibleBeds = allBeds.filter(
    (b) =>
      (!b.room || b.room.id === listing?.id) &&
      (!selectedTypeKey || b.type === selectedTypeKey),
  )
  const toggleBed = (id: string) =>
    setBedIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]))

  const set = <K extends keyof ListingInput>(key: K, value: ListingInput[K]) =>
    setForm((f) => ({...f, [key]: value}))

  const uploadImage = async (file: File) => {
    setUploading(true)
    try {
      const stored = await api.uploadFile(file)
      set('gallery', [...form.gallery, stored.key])
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Upload failed')
    } finally {
      setUploading(false)
    }
  }

  const submit = async () => {
    if (isNew && !form.slug.trim()) {
      toast.error('Slug is required')
      return
    }
    setSaving(true)
    try {
      // Strip read-only fields that the API rejects (they get spread in on edit).
      const {bedType, fromPrice, id, ...rest} = form as ListingInput & {bedType?: unknown; fromPrice?: unknown; id?: string}
      void bedType
      void fromPrice
      void id
      // Drop blank lines kept for editing before sending.
      const payload = {...rest, features: splitLines(rest.features.join('\n')), detailAmenities: splitLines(rest.detailAmenities.join('\n'))}
      let saved: Listing
      if (isNew) {
        saved = await api.createListing(payload)
      } else {
        const {slug, ...updatePayload} = payload
        void slug
        saved = await api.updateListing(listing!.id, updatePayload)
      }
      await api.setRoomBeds(saved.id, bedIds)
      onSaved(saved, isNew)
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Could not save listing')
    } finally {
      setSaving(false)
    }
  }

  return createPortal(
    <div onClick={onClose} style={{position: 'fixed', inset: 0, background: 'rgba(8,10,14,0.5)', zIndex: 2000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 16px', overflowY: 'auto'}}>
      <div onClick={(e) => e.stopPropagation()} style={{background: '#fff', borderRadius: 16, padding: '26px 26px 20px', width: '100%', maxWidth: 720, boxShadow: '0 20px 60px rgba(0,0,0,0.3)'}}>
        <h2 style={{fontSize: 20, fontWeight: 800, color: INK, margin: '0 0 18px'}}>{isNew ? 'New listing' : `Edit ${listing!.name}`}</h2>

        {/* Identity — title mirrors the name; slug auto-generates from it. */}
        <Row>
          <Field label='Name'><input style={inp} value={form.name} onChange={(e) => {
            const name = e.target.value
            setForm((f) => ({...f, name, title: name, slug: isNew && (f.slug === '' || f.slug === slugify(f.name)) ? slugify(name) : f.slug}))
          }} /></Field>
          <Field label='Bed type — sets monthly price'>
            <select style={inp} value={form.bedTypeId ?? ''} onChange={(e) => {set('bedTypeId', e.target.value || undefined); setBedIds([])}}>
              <option value=''>— Select a bed type —</option>
              {bedTypes.map((t) => (
                <option key={t.id} value={t.id}>{t.label} · ${Number(t.pricePerMonth).toFixed(0)}/month</option>
              ))}
            </select>
          </Field>
        </Row>

        <Field label='Beds in this room'>
          {bedIds.length > 0 && (
            <div style={{display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8}}>
              {bedIds.map((id) => {
                const b = allBeds.find((x) => x.id === id)
                return (
                  <span key={id} style={{display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(3,149,64,0.1)', color: ACCENT, fontWeight: 700, fontSize: 13, borderRadius: 999, padding: '4px 6px 4px 11px'}}>
                    {b?.code ?? '—'}{b?.level ? ` · ${b.level}` : ''}
                    <button onClick={() => toggleBed(id)} title='Remove' style={{border: 'none', background: 'rgba(3,149,64,0.18)', color: ACCENT, width: 18, height: 18, borderRadius: '50%', cursor: 'pointer', lineHeight: 1, fontSize: 13, padding: 0}}>×</button>
                  </span>
                )
              })}
            </div>
          )}
          <select
            style={inp}
            value=''
            disabled={!form.bedTypeId}
            onChange={(e) => {
              if (e.target.value) toggleBed(e.target.value)
              e.target.value = ''
            }}
          >
            <option value=''>{form.bedTypeId ? '+ Add a bed…' : 'Pick a bed type first'}</option>
            {eligibleBeds
              .filter((b) => !bedIds.includes(b.id))
              .map((b) => (
                <option key={b.id} value={b.id}>
                  {b.code}{b.level ? ` · ${b.level}` : ''}
                </option>
              ))}
          </select>
        </Field>

        <Row>
          <Field label='Billing period'>
            <select style={inp} value={form.cadence} onChange={(e) => set('cadence', e.target.value)}>
              <option value='Monthly'>Monthly</option>
              <option value='Per day'>Per day</option>
            </select>
          </Field>
          <Field label='Baths'>
            <select style={inp} value={form.baths} onChange={(e) => set('baths', e.target.value)}>
              {[1, 2, 3, 4].map((n) => (
                <option key={n} value={`${n} Bath${n === 1 ? '' : 's'}`}>{n} Bath{n === 1 ? '' : 's'}</option>
              ))}
            </select>
          </Field>
        </Row>

        <Field label='Features (one per line)'>
          <textarea style={{...inp, minHeight: 80}} value={form.features.join('\n')} onChange={(e) => set('features', e.target.value.split('\n'))} />
        </Field>

        <Field label='Description'><textarea style={{...inp, minHeight: 90}} value={form.description} onChange={(e) => set('description', e.target.value)} /></Field>

        <Field label='Location / address (shown on the room map)'>
          <input style={inp} value={form.address ?? ''} placeholder='e.g. 601 S Devon Ave, Wayne, PA 19087' onChange={(e) => set('address', e.target.value)} />
        </Field>

        <Row>
          <Field label='Size'><input style={inp} value={form.size} onChange={(e) => set('size', e.target.value)} /></Field>
          <Field label='Security deposit'>
            <div style={{position: 'relative'}}>
              <span style={{position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: MUTED, fontSize: 14, pointerEvents: 'none'}}>$</span>
              <input style={{...inp, paddingLeft: 22}} value={form.deposit.replace(/^\$/, '')} placeholder='100'
                onChange={(e) => set('deposit', '$' + e.target.value.replace(/[^0-9.]/g, ''))} />
            </div>
          </Field>
        </Row>

        <Field label='Detail amenities (one per line)'>
          <textarea style={{...inp, minHeight: 80}} value={form.detailAmenities.join('\n')} onChange={(e) => set('detailAmenities', e.target.value.split('\n'))} />
        </Field>

        {/* Guidelines */}
        <Field label='Terms & guidelines'>
          <RepeatRows
            rows={form.rules}
            onChange={(rows) => set('rules', rows)}
            blank={{allowed: false, text: ''} as ListingRule}
            render={(row, update) => (
              <>
                <select style={{...inp, flex: '0 0 120px'}} value={row.allowed ? 'yes' : 'no'} onChange={(e) => update({...row, allowed: e.target.value === 'yes'})}>
                  <option value='no'>✕ Not allowed</option>
                  <option value='yes'>✓ Allowed</option>
                </select>
                <input style={{...inp, flex: 1}} placeholder='Guideline text' value={row.text} onChange={(e) => update({...row, text: e.target.value})} />
              </>
            )}
          />
        </Field>

        {/* Gallery */}
        <Field label='Gallery images (first is the card image)'>
          <div style={{display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 10}}>
            {form.gallery.map((g, i) => (
              <div key={`${g}-${i}`} style={{position: 'relative', width: 92, height: 68, borderRadius: 8, overflow: 'hidden', border: '1px solid #e5e7eb'}}>
                <img src={listingImageSrc(g)} alt='' style={{width: '100%', height: '100%', objectFit: 'cover', display: 'block'}} />
                <button onClick={() => set('gallery', form.gallery.filter((_, idx) => idx !== i))} title='Remove' style={{position: 'absolute', top: 2, right: 2, width: 20, height: 20, borderRadius: '50%', border: 'none', background: 'rgba(0,0,0,0.6)', color: '#fff', cursor: 'pointer', lineHeight: 1, fontSize: 13}}>×</button>
              </div>
            ))}
          </div>
          <label style={{...btnGhost, display: 'inline-flex', alignItems: 'center', gap: 8, cursor: uploading ? 'wait' : 'pointer'}}>
            {uploading ? <Spinner size={13} /> : '＋'} Upload image
            <input type='file' accept='image/*' hidden disabled={uploading} onChange={(e) => {
              const file = e.target.files?.[0]
              if (file) void uploadImage(file)
              e.target.value = ''
            }} />
          </label>
        </Field>

        {/* Publish + order */}
        <Row>
          <Field label='Sort order'><input type='number' style={inp} value={form.sortOrder ?? 0} onChange={(e) => set('sortOrder', Number(e.target.value))} /></Field>
          <Field label='Published'>
            <label style={{display: 'flex', alignItems: 'center', gap: 8, padding: '9px 0', fontSize: 14, color: INK, cursor: 'pointer'}}>
              <input type='checkbox' checked={form.published ?? true} onChange={(e) => set('published', e.target.checked)} style={{width: 16, height: 16, accentColor: ACCENT}} />
              Visible on the public site
            </label>
          </Field>
        </Row>

        <div style={{display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18, paddingTop: 16, borderTop: '1px solid #f1f3f7'}}>
          <button onClick={onClose} style={btnGhost} disabled={saving}>Cancel</button>
          <button onClick={submit} style={{...btnPrimary, minWidth: 120}} disabled={saving}>
            {saving ? <Spinner size={14} /> : isNew ? 'Create listing' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}

// ─── Small helpers ──────────────────────────────────────────────────────
const splitLines = (v: string): string[] => v.split('\n').map((s) => s.trim()).filter(Boolean)

const Row: FC<{children: React.ReactNode}> = ({children}) => (
  <div style={{display: 'flex', gap: 12, flexWrap: 'wrap'}}>{children}</div>
)

const Field: FC<{label: string; children: React.ReactNode}> = ({label, children}) => (
  <div style={{flex: 1, minWidth: 140, marginBottom: 14}}>
    <label style={{display: 'block', fontSize: 12, fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6}}>{label}</label>
    {children}
  </div>
)

function RepeatRows<T>({rows, onChange, blank, render}: {
  rows: T[]
  onChange: (rows: T[]) => void
  blank: T
  render: (row: T, update: (next: T) => void) => React.ReactNode
}) {
  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: 8}}>
      {rows.map((row, i) => (
        <div key={i} style={{display: 'flex', gap: 8, alignItems: 'center'}}>
          {render(row, (next) => onChange(rows.map((r, idx) => (idx === i ? next : r))))}
          <button onClick={() => onChange(rows.filter((_, idx) => idx !== i))} title='Remove' style={{flex: '0 0 auto', width: 34, height: 34, borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', color: '#d9214e', cursor: 'pointer'}}>×</button>
        </div>
      ))}
      <button onClick={() => onChange([...rows, {...blank}])} style={{...btnGhost, alignSelf: 'flex-start'}}>+ Add</button>
    </div>
  )
}

const btnBase: React.CSSProperties = {fontSize: 13, fontWeight: 600, borderRadius: 8, padding: '8px 14px', cursor: 'pointer', border: '1px solid transparent'}
const btnPrimary: React.CSSProperties = {...btnBase, background: ACCENT, color: '#fff'}
const btnGhost: React.CSSProperties = {...btnBase, background: '#fff', border: '1px solid #e5e7eb', color: INK}
const btnDanger: React.CSSProperties = {...btnBase, background: 'rgba(217,33,78,0.1)', color: '#d9214e'}
const inp: React.CSSProperties = {width: '100%', padding: '9px 11px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, color: INK, background: '#fff', boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit'}

export {ListingsPage}
